import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    Switch,
    TextField,
} from "@mui/material";
import PostData from "../../../services/PostData";
import SnackbarEx from "../../Helpers/SnackbarEx.jsx";
import CatalogPageShell from "../../Products/CatalogPageShell.jsx";
import {
    catalogDangerButtonSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
} from "../../Products/catalogGridStyles.js";
import SingleListingShopify from "./SingleListingShopify.jsx";
import NewShopifyVarListing from "./NewShopifyVarListing.jsx";
import "../uploadProducts.css";

export default function NewShopifyListing() {
    const navigate = useNavigate();
    const [variants, setVariants] = useState(false);
    const [varItemTitle, setVarItemTitle] = useState("");
    const [openTestSnack, setOpenTestSnack] = useState({
        color: "",
        message: "",
        open: false,
    });
    const [submitLoad, setSubmitLoad] = useState(false);
    const [attributeInput, setAttributeInput] = useState("");
    const [productAttributes, setProductAttributes] = useState([]);

    const formFields = [
        { name: "title", type: "text", initialValue: "" },
        { name: "upc", type: "text", initialValue: "" },
        { name: "brand", type: "text", initialValue: "" },
        { name: "size", type: "text", initialValue: "" },
        { name: "color / style", type: "text", initialValue: "" },
        { name: "weight", type: "text", initialValue: "" },
        { name: "height", type: "text", initialValue: "" },
        { name: "depth", type: "text", initialValue: "" },
        { name: "width", type: "text", initialValue: "" },
        { name: "price", type: "number", initialValue: "" },
        { name: "images", type: "text", initialValue: [] },
        { name: "sku", type: "text", initialValue: "" },
        { name: "quantity", type: "number", initialValue: 0 },
    ];

    const initalState = formFields.reduce(
        (acc, next) => ({ ...acc, [next.name]: next.initialValue }),
        {}
    );
    const [formData, setFormData] = useState([initalState]);
    const [isFormValid, setIsFormValid] = useState(false);

    const handleAddAttribute = (event) => {
        if (event.key === "Enter" && attributeInput.trim() !== "") {
            event.preventDefault();
            if (!productAttributes.includes(attributeInput.trim())) {
                setProductAttributes([...productAttributes, attributeInput.trim()]);
            }
            setAttributeInput("");
        }
    };

    const handleDeleteAttribute = (chipToDelete) => () => {
        setProductAttributes((chips) => chips.filter((chip) => chip !== chipToDelete));
    };

    const handleAddVariant = () => {
        setFormData((prev) => [...prev, initalState]);
    };

    const handleRemoveVariant = (indexToRemove) => {
        setFormData((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleUpdateVariant = (data, index) => {
        setFormData((prev) => {
            const next = [...prev];
            next[index] = data;
            return next;
        });
    };

    useEffect(() => {
        if (!variants) {
            const { title, brand, price, sku } = formData[0] || {};
            setIsFormValid(Boolean(title && brand && price && sku));
            return;
        }
        const allOk =
            Boolean(varItemTitle) &&
            productAttributes.length > 0 &&
            formData.length >= 2 &&
            formData.every((row) => row?.sku && row?.price);
        setIsFormValid(allOk);
    }, [formData, variants, varItemTitle, productAttributes]);

    const handleSubmit = async () => {
        setSubmitLoad(true);
        const submitData = {
            productInfo: formData,
            variantUpload: variants,
            variantAspects: productAttributes,
            ...(varItemTitle && { varTitle: varItemTitle }),
        };
        const { success, error } = await PostData(
            submitData,
            "broylesllc/create-shopify-sku",
            null
        );
        if (success) {
            setSubmitLoad(false);
            setOpenTestSnack({
                color: "success",
                message: "Item added to Shopify.",
                open: true,
            });
            setTimeout(() => navigate(-1), 1000);
        } else {
            setSubmitLoad(false);
            setOpenTestSnack({
                color: "error",
                message: error?.response?.data || "Shopify upload failed",
                open: true,
            });
        }
    };

    return (
        <CatalogPageShell
            title="New Shopify Product"
            subtitle="Create a single or variant listing from a product SKU."
        >
            <div className="upload-stack">
                <div className="upload-toolbar">
                    <Button
                        size="small"
                        startIcon={<ArrowBackOutlinedIcon />}
                        onClick={() => navigate(-1)}
                        sx={catalogQuietButtonSx}
                        variant="outlined"
                    >
                        Back
                    </Button>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={variants}
                                onChange={() => setVariants((v) => !v)}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#0d9488",
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                            backgroundColor: "#0d9488",
                                        },
                                }}
                            />
                        }
                        label="Variant product"
                        labelPlacement="start"
                        sx={{
                            m: 0,
                            "& .MuiTypography-root": {
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#1c2430",
                            },
                        }}
                    />
                </div>

                <div className="upload-form-scroll">
                    {variants ? (
                        <div className="mb-3 d-flex flex-wrap align-items-center gap-2 justify-content-end">
                            {productAttributes.map((data) => (
                                <Chip
                                    key={data}
                                    label={data}
                                    onDelete={handleDeleteAttribute(data)}
                                    sx={{
                                        borderRadius: "8px",
                                        fontWeight: 600,
                                        backgroundColor: "#f1f5f9",
                                    }}
                                />
                            ))}
                            <TextField
                                size="small"
                                required
                                variant="outlined"
                                helperText="Press enter to set variant"
                                label="Variant type(s)"
                                placeholder="Color, Size …"
                                value={attributeInput}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setAttributeInput(
                                        (
                                            val.charAt(0).toUpperCase() +
                                            val.slice(1).replace(/([A-Z])/g, " $1")
                                        ).trim()
                                    );
                                }}
                                onKeyDown={handleAddAttribute}
                                sx={{
                                    ...catalogSearchFieldSx,
                                    minWidth: 220,
                                }}
                            />
                        </div>
                    ) : null}

                    {!variants ? (
                        <SingleListingShopify
                            submitLoad={submitLoad}
                            updateFormData={(d) => setFormData(d)}
                        />
                    ) : (
                        <>
                            <p className="upload-section-label">Master Item Title</p>
                            <TextField
                                value={varItemTitle}
                                size="small"
                                onChange={(e) => setVarItemTitle(e.target.value)}
                                sx={{
                                    ...catalogSearchFieldSx,
                                    width: { xs: "100%", sm: "50%" },
                                    mb: 2,
                                }}
                            />
                            {formData.map((variantData, index) => (
                                <NewShopifyVarListing
                                    key={index}
                                    varAttributes={productAttributes}
                                    submitLoad={submitLoad}
                                    formData={variantData}
                                    updateFormData={(d) => handleUpdateVariant(d, index)}
                                />
                            ))}
                            <div className="d-flex justify-content-end gap-2 mb-3">
                                {formData.length > 1 ? (
                                    <Button
                                        startIcon={<DeleteIcon />}
                                        variant="outlined"
                                        sx={catalogDangerButtonSx}
                                        onClick={() =>
                                            handleRemoveVariant(formData.length - 1)
                                        }
                                    >
                                        Remove Variant
                                    </Button>
                                ) : null}
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="contained"
                                    sx={catalogPrimaryButtonSx}
                                    onClick={handleAddVariant}
                                >
                                    Add Variant
                                </Button>
                            </div>
                        </>
                    )}
                </div>

                <div className="upload-toolbar" style={{ borderTop: "1px solid #e2e8f0" }}>
                    <div className="catalog-toolbar-spacer" />
                    <Button
                        variant="contained"
                        disabled={!isFormValid || submitLoad}
                        onClick={handleSubmit}
                        startIcon={
                            submitLoad ? <CircularProgress size={14} color="inherit" /> : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        Upload to Shopify
                    </Button>
                </div>
            </div>
            {openTestSnack.open && (
                <SnackbarEx
                    color={openTestSnack.color}
                    message={openTestSnack.message}
                    open={openTestSnack.open}
                    close={() =>
                        setOpenTestSnack({ color: "", message: "", open: false })
                    }
                />
            )}
        </CatalogPageShell>
    );
}
